import dynamic from 'next/dynamic';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { MdArrowForward, MdOutlineArrowBack } from 'react-icons/md';
import BtnConfirm from 'ui/components/atoms/BtnConfirm';
import StructuredData from 'ui/components/atoms/StructuredData';
import BreadCrumbs, { IBreadcrumbs } from 'ui/components/molecules/Breadcrumbs';
import DetailPromoAnotherPromoSection from 'ui/components/organism/DetailPromoAnotherPromoSection';
import DetailPromoBodySection from 'ui/components/organism/DetailPromoBodySection';
import DetailPromoHeroSection from 'ui/components/organism/DetailPromoHeroSection';
import SocialMediaLinksSection from 'ui/components/organism/SocialMediaLinksSection';
import eventClickInterest from 'ui/lib/gtm/eventClickInterest';
import { convertStringToSlug } from '../../../helpers/utils';
import useElementVisibility from '../../../hooks/useElementVisibility';
import { IRootState } from '../../../store';
import { useRouter } from '../../../hooks/useCustomRouter';
import { Article } from '../../../interface';
import LoyaltyProgramSection from '../../molecules/LoyaltyProgramSection';
import useImpressionPromo from '../../../hooks/emarsys/promo/useImpressionPromo';

declare const window: WindowTypes;

const ErrorScreen = dynamic(() => import('ui/components/molecules/ErrorScreen'), {
  ssr: false,
});

const DetailPromoInquiryFormSection = dynamic(
  () => import('ui/components/organism/DetailPromoInquiryFormSection'),
  {
    ssr: false,
  }
);

const ModalPromoExpired = dynamic(() => import('ui/components/molecules/ModalPromoExpired'), {
  ssr: false,
});

const TanyaTasia = dynamic(() => import('ui/components/atoms/TanyaTasia'), {
  ssr: false,
});

const InquiryFormMobileSection = dynamic(
  () => import('ui/components/organism/InquiryFormMobileSection'),
  {
    ssr: false,
  }
);

interface IProps {
  promo: Article;
  anotherPromo: Article[];
  errorCode?: number;
  isNativeView?: boolean;
  breadcrumbItems: IBreadcrumbs[];
  sourcePage?: string;
  branchCode?: string;
  cityOfThePromo?: {
    name: string;
    isocode: string;
    province: {
      name: string;
      isocode: string;
    };
  };
  isBranchTypeBP?: boolean;
  showLoyaltyCard?: boolean;
  actionClick?: (buttonName: string, sectionName: string) => void;
  pageTitle?: string;
  astrapointsValue?: string;
  feature?: string;
}

const PromoDetailPageTemplate = ({
  promo,
  anotherPromo,
  errorCode,
  breadcrumbItems,
  sourcePage,
  branchCode,
  isNativeView = false,
  cityOfThePromo = null,
  isBranchTypeBP = false,
  showLoyaltyCard = false,
  actionClick,
  pageTitle,
  astrapointsValue = '',
  feature = '',
}: IProps) => {
  const { isGTMReady } = useSelector((state: any) => state.dataGTM || {});
  const { isLoggedIn } = useSelector((state: IRootState) => state.auth) || {};
  const { isMobileScreen } = useSelector((state: IRootState) => state.page) || {};
  const [formOpen, setFormOpen] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [isButtonInquiryVisible, setIsButtonInquiryVisible] = useState(false);
  const buttonInquiryListRef = useRef(null);
  const initialButtonPosition = useRef(0);
  const isButtonStuck = useElementVisibility(buttonInquiryListRef);

  const router = useRouter();

  const basePathUrl = process.env.NEXT_PUBLIC_BASEPATH_URL;
  const PUBLIC_URL = process.env.NEXT_PUBLIC_URL;

  // Temporary check condition if inquiry form is hidden
  const isInquiryFormHidden = useMemo(
    () => router?.query?.kategori !== 'mobil-baru' && (router?.query?.salesmanSlug ? false : true),
    [router?.query]
  );

  useEffect(() => {
    const categorySlug = convertStringToSlug(promo?.contentCategory?.name);
    if (router?.query?.kategori !== categorySlug && !errorCode) {
      router.push(`${basePathUrl}/${categorySlug}/${router?.query?.slug}`, undefined, {
        shallow: true,
      });
    }
  }, []);

  useEffect(() => {
    // Show modal if promo status is EXPIRED
    if (promo && promo.status === 'EXPIRED') {
      setModalVisible(true);
    }
  }, [promo]);

  useEffect(() => {
    if (isNativeView) {
      window?.androidApp?.showToolbar();
      window?.webkit?.messageHandlers?.iosApp?.postMessage('showToolbar');
    }

    const handleScroll = () => {
      // calculates the top position of a button element relative to the viewport
      const currentButtonPosition = buttonInquiryListRef?.current?.getBoundingClientRect()?.top;
      const scrollDifference = initialButtonPosition?.current - currentButtonPosition;

      // if the difference is greater than 100px, set setIsButtonInquiryVisible to false
      if (scrollDifference > 25 && currentButtonPosition <= initialButtonPosition.current) {
        setIsButtonInquiryVisible(true);
      } else {
        setIsButtonInquiryVisible(false);
      }
    };

    // save the initial position of the button element
    initialButtonPosition.current = buttonInquiryListRef?.current?.getBoundingClientRect()?.top;

    // add event listener to detect scroll
    window.addEventListener('scroll', handleScroll);

    // clean up event listener when component unmount
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleOpenInquiryForm = () => {
    setFormOpen(true);
    eventClickInterest(promo);
    window?.androidApp?.hideToolbar();
    window?.webkit?.messageHandlers?.iosApp?.postMessage('hideToolbar');

    window?.androidApp?.clickAskOfferPromo();
    window?.webkit?.messageHandlers?.iosApp?.postMessage('clickAskOfferPromo');
  };

  const handleCloseInquiryForm = () => {
    setFormOpen(false);
    window?.androidApp?.showToolbar();
    window?.webkit?.messageHandlers?.iosApp?.postMessage('showToolbar');
  };

  // Error page
  if (errorCode) {
    return <ErrorScreen errorCode={errorCode} />;
  }

  useImpressionPromo(buttonInquiryListRef, promo as Article, breadcrumbItems, 'minta penawaran');

  return (
    <>
      {/* Data structure for SEO */}
      <StructuredData breadcrumbs={breadcrumbItems} />

      <ModalPromoExpired
        visible={modalVisible}
        setVisible={(boolean) => setModalVisible(boolean)}
      />

      {/* Breadcrumb for web screen */}
      {!isNativeView && (
        <>
          <BreadCrumbs isMobileScreen={false} items={breadcrumbItems} />

          <div
            className={`container flex justify-start items-center w-full pt-2 pb-2 sticky top-[60px] md:top-[111px] z-30 bg-white md:hidden`}
          >
            <button className="h-full" onClick={() => router.back()} aria-label="back button">
              <MdOutlineArrowBack size={24} color="#3D4043" />
            </button>
          </div>
        </>
      )}

      <div className="relative">
        {/* Main content */}
        <div className="lg:container xl:px-0">
          <div
            className={`grid grid-cols-1 gap-x-6 gap-y-3 ${
              isInquiryFormHidden || isBranchTypeBP
                ? showLoyaltyCard
                  ? 'lg:grid-cols-[2fr_1fr]'
                  : 'lg:grid-cols-1 lg:max-w-[820px] lg:mx-auto'
                : 'lg:grid-cols-[2fr_1fr]'
            }`}
          >
            {/* Col 1: Detail Promo */}
            <div className="disableTextSelection">
              {/* Detail Promo Hero Section */}
              {promo.heroImageLink && (
                <DetailPromoHeroSection
                  bannerLink={promo?.bannerLink}
                  imageUrl={promo?.heroImageLink || ''}
                  altImage={promo.altImage}
                />
              )}

              {/* Detail Promo Body Section */}
              <DetailPromoBodySection
                promo={promo}
                actionClick={(buttonName) => actionClick(buttonName, 'bagikan')}
                impressionData={{
                  pageTitle: pageTitle || '',
                  breadCrumb: breadcrumbItems,
                }}
              />
            </div>

            {/* Col 2: Inquiry from section for desktop */}
            {!isInquiryFormHidden && !isBranchTypeBP && !isMobileScreen && (
              <div className="container hidden md:mt-5 lg:p-0 md:block">
                <DetailPromoInquiryFormSection
                  dataPromo={promo}
                  sourcePage={sourcePage}
                  branchCode={branchCode}
                  cityOfThePromo={cityOfThePromo}
                  isNativeView={isNativeView}
                  actionClick={(buttonName: string) => actionClick(buttonName, 'minta penawaran')}
                  impressionData={{
                    pageTitle: pageTitle || '',
                    breadCrumb: breadcrumbItems,
                    promo,
                  }}
                  feature={feature}
                />
              </div>
            )}

            {showLoyaltyCard && (
              <section className="container lg:p-0 lg:mt-5">
                <LoyaltyProgramSection
                  isLogin={isLoggedIn}
                  astrapointsValue={astrapointsValue || ''}
                />
              </section>
            )}
          </div>
          <div id="doxa-form" />
        </div>

        {/* Tanya Tasia & Back to Top Button */}
        {isGTMReady &&
          (!isButtonInquiryVisible && !isInquiryFormHidden ? (
            <TanyaTasia
              backToTopClassName={`${isMobileScreen ? '!bottom-[158px]' : '!bottom-[30px]'}`}
            />
          ) : (
            <TanyaTasia
              backToTopClassName={`${isMobileScreen ? '!bottom-[86px]' : '!bottom-[30px]'}`}
            />
          ))}

        {/* CTA Button for mobile screen */}
        {!isInquiryFormHidden && !isBranchTypeBP && (
          <div
            className={`sticky w-full z-20 mt-2.5 md:hidden ${
              isNativeView ? 'bottom-0' : 'bottom-[69px]'
            } ${isButtonStuck ? 'top-0' : ''}`}
            ref={buttonInquiryListRef}
          >
            <BtnConfirm
              id="btn-mobile-saya-tertarik"
              block={true}
              className="bg-supportiveRed text-white h-[72px]"
              size="large"
              iconType="fill"
              onClick={handleOpenInquiryForm}
            >
              <div className="flex justify-end items-center gap-3">
                <div className="flex flex-col text-end">
                  <p className="text-base font-bold uppercase">Saya Tertarik</p>
                  <p className="text-xs font-medium">Dapatkan penawaran terbaik dari Auto2000</p>
                </div>
                <MdArrowForward className="text-reliableBlack3" size={20} />
              </div>
            </BtnConfirm>
          </div>
        )}
      </div>

      <div>
        {/* Additional content */}
        <div className="container xl:px-0">
          {/* Another promo */}
          <DetailPromoAnotherPromoSection
            anotherPromo={anotherPromo}
            actionClick={(buttonName: string) => actionClick(buttonName, 'promo lainnya')}
            impressionData={{
              pageTitle: pageTitle || '',
              breadCrumb: breadcrumbItems,
              promo,
            }}
          />
        </div>

        {/* Social media links */}
        <div className="mt-[38px] md:mt-0">
          <SocialMediaLinksSection
            shareUrl={`${PUBLIC_URL}/${promo.slug}`}
            title={promo.titlePage}
            copyLink={promo}
            actionClick={(buttonName: string) => actionClick(buttonName, 'bagikan')}
            impressionData={{
              pageTitle: pageTitle || '',
              breadCrumb: breadcrumbItems,
              promo,
            }}
            feature="PROMO"
          />
        </div>

        {/* Breadcrumb for mobile screen */}
        {!isNativeView && (
          <BreadCrumbs
            isMobileScreen={true}
            items={breadcrumbItems}
            breadcrumbsClassName={'mt-[38px]'}
          />
        )}
      </div>

      {/* Inquiry form mobile section */}
      {isMobileScreen && !isBranchTypeBP && (
        <div className="block lg:hidden">
          <InquiryFormMobileSection
            formOpen={formOpen}
            onClose={handleCloseInquiryForm}
            promo={promo}
            sourcePage={sourcePage}
            branchCode={branchCode}
            cityOfThePromo={cityOfThePromo}
            actionClick={(buttonName: string) => actionClick(buttonName, 'minta penawaran')}
          />
        </div>
      )}
    </>
  );
};

export default PromoDetailPageTemplate;
